import { toast } from "sonner";
import apiRequest from "./apiRequest";

/**
 * Ouvre un fichier servi par un endpoint protégé (PDF, image…) dans un
 * nouvel onglet, en transportant l'auth Bearer.
 *
 * <p>Le pattern « ancre `<a href={apiUrl} target="_blank">` » ne marche
 * plus depuis que les endpoints exigent le header `Authorization` —
 * une navigation top-level n'attache aucun header custom. La parade
 * canonique : fetch authentifié → Blob → `URL.createObjectURL` →
 * ouverture en blob URL.
 *
 * <h3>Choix d'implémentation</h3>
 * <ul>
 *   <li><strong>`apiRequest` réutilisé</strong> avec
 *       {@code responseType: "blob"} — héritage du refresh préemptif,
 *       du rejeu sur 401 et du logout automatique en cas de session
 *       morte. Sans ça, un PDF échoue silencieusement quand le token
 *       expire pendant que l'utilisateur lit la page.</li>
 *   <li><strong>Onglet pré-ouvert synchrone</strong> via
 *       {@code window.open("about:blank", "_blank")} <em>avant</em> le
 *       {@code await}. Safari et Firefox bloquent {@code window.open}
 *       déclenché après une await chain (le user-gesture context est
 *       perdu) — pré-ouvrir préserve le geste. On redirige ensuite
 *       l'onglet via {@code placeholder.location.href = blobUrl}.</li>
 *   <li><strong>Toast Sonner sur erreur</strong> + fermeture du
 *       placeholder pour ne pas laisser un onglet blanc orphelin.</li>
 *   <li><strong>Revoke différé 60 s</strong> — laisse le temps au
 *       viewer PDF de finir de charger avant de libérer le blob.</li>
 * </ul>
 *
 * @param {string} path           Chemin relatif (ex. `"/users/patient-card-pdf/42"`).
 *                                Sera préfixé par {@code config.API} via apiRequest.
 * @param {{
 *   accept?: string,
 *   loadingMessage?: string,
 *   download?: string,
 * }} [options]
 *   - {@code accept}          : header `Accept` — défaut "application/pdf".
 *   - {@code loadingMessage}  : toast affiché pendant le téléchargement.
 *                               Si absent, pas de toast d'attente.
 *   - {@code download}        : si renseigné, déclenche un download
 *                               forcé (via `<a download>`) au lieu
 *                               d'ouvrir un onglet. La valeur est
 *                               le nom de fichier proposé.
 */
export async function openProtectedFile(path, options = {}) {
  const {
    accept = "application/pdf",
    loadingMessage,
    download
  } = options;

  // Pré-ouverture synchrone — préserve le user-gesture context pour
  // contourner les popup blockers. null si déjà bloqué (l'utilisateur
  // a un blocker strict) → on retombera sur un download forcé.
  const placeholder = download ? null : window.open("about:blank", "_blank");

  let toastId;
  if (loadingMessage) {
    toastId = toast.loading(loadingMessage);
  }

  const result = await apiRequest.get(path, {
    responseType: "blob",
    headers: { Accept: accept }
  });

  if (!result.ok) {
    if (placeholder && !placeholder.closed) placeholder.close();
    if (toastId) toast.dismiss(toastId);
    return false;
  }

  const blob = result.data;
  const blobUrl = URL.createObjectURL(blob);

  if (download) {
    // Mode download forcé : <a download="..."> programmatique.
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = download;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else if (placeholder && !placeholder.closed) {
    placeholder.location.href = blobUrl;
  } else {
    // Popup bloqué : retombe sur un download au lieu de perdre le PDF.
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = "document.pdf";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  if (toastId) toast.dismiss(toastId);

  // Revoke après 60 s — laisse le viewer PDF finir de lire le blob.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  return true;
}

export default openProtectedFile;
